
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ICONS } from '../constants';
import { 
    loginUser, 
    registerUser, 
    updateUserProfile, 
    verifyUserEmail, 
    logoutUser, 
    passwordReset, 
    signInWithGoogle,
    createUserDocument 
} from '../services/firebase';
import { LegalModal } from './LegalDocs';
import clsx from 'clsx';

interface LandingPageProps {
  onEnter: () => void;
}

type AuthView = 'LOGIN' | 'REGISTER' | 'VERIFY_EMAIL' | 'FORGOT_PASSWORD' | 'RESET_SENT';

const LandingPage: React.FC<LandingPageProps> = ({ onEnter }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Legal Modal
  const [activeLegalModal, setActiveLegalModal] = useState<'TERMS' | 'PRIVACY' | null>(null);

  // Auth Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  
  const [authView, setAuthView] = useState<AuthView>('LOGIN');
  const [errorMsg, setErrorMsg] = useState('');
  const [tempEmail, setTempEmail] = useState(''); 

  const resetForm = () => {
      setErrorMsg('');
      setPassword('');
      setRepeatPassword('');
  };

  const switchTo = (view: AuthView) => {
      setAuthView(view);
      resetForm();
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');
    
    try {
        if (authView === 'LOGIN') {
            const userCredential = await loginUser(email, password);
            const user = userCredential.user;

            if (!user.emailVerified) {
                setTempEmail(user.email || email);
                await logoutUser();
                switchTo('VERIFY_EMAIL');
            } else {
                await createUserDocument(user);
                onEnter();
            }

        } else if (authView === 'REGISTER') {
            if (password !== repeatPassword) throw new Error("Passwords do not match");
            if (!name.trim()) throw new Error("Name is required");

            const userCredential = await registerUser(email, password);
            const user = userCredential.user;
            
            await updateUserProfile(user, name);
            await createUserDocument(user, { name: name });
            await verifyUserEmail(user);
            
            setTempEmail(email);
            await logoutUser();
            switchTo('VERIFY_EMAIL');
        }
    } catch (error: any) {
        console.error("Authentication failed", error);
        let msg = "Authentication failed.";
        
        if (authView === 'LOGIN') {
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
                msg = "Password or Email Incorrect";
            } else if (error.code === 'auth/too-many-requests') {
                msg = "Too many attempts. Try again later.";
            } else {
                msg = error.message;
            }
        } else {
            if (error.code === 'auth/email-already-in-use') {
                msg = "User already exists. Sign in?";
                setTimeout(() => switchTo('LOGIN'), 1500);
            } else if (error.message === "Passwords do not match") {
                msg = "Passwords do not match.";
            } else if (error.code === 'auth/weak-password') {
                msg = "Password should be at least 6 characters.";
            } else {
                msg = error.message;
            }
        }
        setErrorMsg(msg);
    } finally {
        setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
      setIsLoading(true);
      setErrorMsg('');
      try {
          const result = await signInWithGoogle();
          await createUserDocument(result.user);
          onEnter();
      } catch (error: any) {
          console.error("Google Sign In failed", error);
          if (error.code === 'auth/unauthorized-domain') {
              setErrorMsg(`Domain unauthorized (${window.location.hostname}). Add to Firebase Console.`);
          } else if (error.code === 'auth/popup-closed-by-user') {
              setErrorMsg("Sign-in cancelled.");
          } else {
              setErrorMsg(error.message);
          }
      } finally {
          setIsLoading(false);
      }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);
      setErrorMsg('');
      try {
          await passwordReset(email);
          setTempEmail(email);
          switchTo('RESET_SENT');
      } catch (error: any) {
          setErrorMsg(error.message);
      } finally {
          setIsLoading(false);
      }
  };

  const GoogleIcon = () => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Google Logo">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
  );

  return (
    <div className="h-[100dvh] w-full bg-black text-white flex flex-col relative overflow-hidden font-sans">
      
      {/* --- CINEMATIC FROSTED GLASS BACKGROUND --- */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none bg-black">
        <motion.div
            initial={{ scale: 1.1 }}
            animate={{ scale: 1.0 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
            className="absolute inset-0"
        >
            <img 
                src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2600&auto=format&fit=crop" 
                alt="Background: Abstract Fluid" 
                className="w-full h-full object-cover opacity-60 grayscale contrast-125" 
            />
        </motion.div>
        <div className="absolute inset-0 backdrop-blur-[14px] bg-black/40"></div>
        <div className="absolute inset-0 bg-noise opacity-[0.06] mix-blend-overlay"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80"></div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-between w-full max-w-7xl mx-auto px-6 relative z-10 pt-5 md:pt-10 overflow-y-auto no-scrollbar">
        
        <div className="flex flex-col items-center justify-center text-center w-full">
            {/* HERO VIDEO REPLACE ICON */}
            <div className="mt-5 md:mt-10 mb-10 relative w-full max-w-3xl aspect-video rounded-[24px] overflow-hidden border border-white/20 shadow-[0_0_100px_rgba(255,255,255,0.05)] bg-zinc-900 group">
                <iframe 
                    src="https://player.vimeo.com/video/1148755411?background=1&autoplay=1&loop=1&byline=0&title=0" 
                    className="absolute inset-0 w-full h-full scale-[1.3] pointer-events-none"
                    frameBorder="0" 
                    allow="autoplay; fullscreen" 
                    title="Cinematic Hero Video"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none"></div>
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>
                <div className="absolute inset-0 border-[0.5px] border-white/20 rounded-[24px] pointer-events-none"></div>
            </div>

            <h1 className="text-4xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6 text-white uppercase drop-shadow-2xl leading-[0.9]">
              Turn Any Photo<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-400 to-zinc-600">Into Studio Quality</span><br/>In Seconds
            </h1>

            <p className="text-xs md:text-sm text-zinc-300 font-mono tracking-[0.2em] max-w-xl mb-12 uppercase drop-shadow-md">
              Autonomous AI photography. No skills required. Fix lighting, detail, and style instantly.
            </p>

            <div className="flex flex-col items-center gap-4">
                <button
                    onClick={() => {
                        switchTo('LOGIN');
                        setShowLogin(true);
                    }}
                    className="group relative px-10 py-4 bg-white/10 backdrop-blur-md text-white border border-white/20 font-bold tracking-[0.2em] uppercase overflow-hidden hover:bg-white hover:text-black transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-white rounded-sm shadow-xl"
                    aria-label="Try 5 Free Reshoots"
                >
                    <span className="relative z-10 flex items-center gap-3 text-xs">
                        Try 5 Free Reshoots <ICONS.ArrowRight className="w-3 h-3" />
                    </span>
                </button>
                <p className="text-[10px] md:text-xs text-zinc-400 font-bold tracking-[0.2em] uppercase">
                    No Credit Card Required - Instant Access
                </p>
            </div>
        </div>
        
        {/* Footer with Logo & Wordmark - Perfectly centered on desktop */}
        <div className="flex flex-col md:flex-row w-full items-center md:items-end pb-10 md:pb-12 mt-20 z-20 px-4 md:px-0 gap-8 md:gap-0">
            {/* Left Slot: Legal Links */}
            <div className="flex-1 flex justify-center md:justify-start gap-6 text-[10px] uppercase tracking-widest text-zinc-500 font-bold order-2 md:order-1">
                <button onClick={() => setActiveLegalModal('TERMS')} className="hover:text-white transition-colors">Terms</button>
                <button onClick={() => setActiveLegalModal('PRIVACY')} className="hover:text-white transition-colors">Privacy</button>
            </div>
            
            {/* Center Slot: Brand Identity */}
            <div className="flex flex-col items-center gap-2 order-1 md:order-2 shrink-0">
                <ICONS.Aperture className="w-8 h-8 text-white stroke-[1]" />
                <span className="text-sm font-bold tracking-[0.4em] uppercase text-white">Reshooter</span>
            </div>

            {/* Right Slot: System Status */}
            <div className="flex-1 hidden md:flex items-center justify-end gap-2 text-[10px] uppercase tracking-widest text-zinc-600 font-bold order-3">
                 <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                 System Operational
            </div>
        </div>
      </main>

      {/* AUTH MODAL */}
      <AnimatePresence>
        {showLogin && (
          <motion.div
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             exit={{ opacity: 0 }}
             className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
             onClick={() => setShowLogin(false)}
          >
             <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="w-full max-w-md bg-black/40 backdrop-blur-3xl ring-1 ring-white/5 rounded-2xl p-8 shadow-2xl relative overflow-hidden"
                onClick={(e) => e.stopPropagation()}
             >
                <button 
                    onClick={() => setShowLogin(false)}
                    className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white transition-colors rounded-full"
                >
                    <ICONS.Close className="w-5 h-5" />
                </button>

                <div className="flex flex-col items-center mb-8">
                    <ICONS.Aperture className="w-10 h-10 text-white stroke-[1] mb-4" />
                    <h2 className="text-xl font-bold uppercase tracking-widest text-white">
                        {authView === 'LOGIN' && 'Access Studio'}
                        {authView === 'REGISTER' && 'Sign Up'}
                        {authView === 'FORGOT_PASSWORD' && 'Recover Access'}
                        {authView === 'VERIFY_EMAIL' && 'Verification'}
                        {authView === 'RESET_SENT' && 'Check Email'}
                    </h2>
                </div>

                {errorMsg && (
                    <div className="mb-6 p-3 bg-red-900/20 border border-red-500/20 rounded text-red-400 text-xs text-center font-mono">
                        {errorMsg}
                    </div>
                )}

                {authView === 'LOGIN' && (
                    <form onSubmit={handleAuth} className="space-y-4">
                        <input 
                            type="email" 
                            placeholder="Email" 
                            className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <input 
                            type="password" 
                            placeholder="Password" 
                            className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <div className="flex justify-end">
                            <button type="button" onClick={() => switchTo('FORGOT_PASSWORD')} className="text-[10px] text-zinc-500 hover:text-white uppercase tracking-wider">
                                Forgot Password?
                            </button>
                        </div>
                        <button type="submit" disabled={isLoading} className="w-full bg-white text-black font-bold uppercase tracking-[0.15em] py-3 rounded text-xs hover:bg-zinc-200 transition-colors disabled:opacity-50 mt-4">
                            {isLoading ? 'Authenticating...' : 'Initialize'}
                        </button>
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
                            <div className="relative flex justify-center text-xs uppercase"><span className="bg-black/40 px-2 text-zinc-500">Or</span></div>
                        </div>
                        <button type="button" onClick={handleGoogleSignIn} disabled={isLoading} className="w-full bg-white/5 hover:bg-white/10 text-white border border-white/10 font-bold uppercase tracking-[0.15em] py-3 rounded text-xs transition-colors flex items-center justify-center gap-3">
                            <GoogleIcon /> Access via Google
                        </button>
                        <div className="mt-6 text-center">
                            <button type="button" onClick={() => switchTo('REGISTER')} className="text-xs text-zinc-400 hover:text-white uppercase tracking-wider">
                                New User? <span className="underline decoration-zinc-600 underline-offset-4">Sign Up</span>
                            </button>
                        </div>
                    </form>
                )}

                {authView === 'REGISTER' && (
                    <form onSubmit={handleAuth} className="space-y-4">
                        <input type="text" placeholder="Display Name" className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all" value={name} onChange={(e) => setName(e.target.value)} required />
                        <input type="email" placeholder="Email" className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all" value={email} onChange={(e) => setEmail(e.target.value)} required />
                        <input type="password" placeholder="Create Password" className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all" value={password} onChange={(e) => setPassword(e.target.value)} required />
                        <input type="password" placeholder="Confirm Password" className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all" value={repeatPassword} onChange={(e) => setRepeatPassword(e.target.value)} required />
                        <button type="submit" disabled={isLoading} className="w-full bg-white text-black font-bold uppercase tracking-[0.15em] py-3 rounded text-xs hover:bg-zinc-200 transition-colors disabled:opacity-50 mt-4">
                            {isLoading ? 'Signing Up...' : 'Sign Up'}
                        </button>
                        <div className="mt-6 text-center">
                            <button type="button" onClick={() => switchTo('LOGIN')} className="text-xs text-zinc-400 hover:text-white uppercase tracking-wider">
                                Existing User? <span className="underline decoration-zinc-600 underline-offset-4">Login</span>
                            </button>
                        </div>
                    </form>
                )}

                {authView === 'VERIFY_EMAIL' && (
                    <div className="text-center space-y-6">
                        <div className="space-y-2">
                            <p className="text-sm text-zinc-300 leading-relaxed">
                                A verification signal has been sent to <span className="text-white font-mono">{tempEmail}</span>.
                                <br/>Please verify your access key to enter the studio.
                            </p>
                        </div>
                        <button onClick={() => switchTo('LOGIN')} className="w-full bg-white text-black font-bold uppercase tracking-[0.15em] py-3 rounded text-xs hover:bg-zinc-200 transition-colors">
                            Return to Login
                        </button>
                    </div>
                )}

                {authView === 'FORGOT_PASSWORD' && (
                    <form onSubmit={handlePasswordReset} className="space-y-4">
                        <p className="text-xs text-zinc-400 text-center mb-4">Enter your email to receive recovery instructions.</p>
                        <input type="email" placeholder="Email" className="w-full bg-black/50 border border-white/10 rounded px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 transition-all" value={email} onChange={(e) => setEmail(e.target.value)} required />
                         <button type="submit" disabled={isLoading} className="w-full bg-white text-black font-bold uppercase tracking-[0.15em] py-3 rounded text-xs hover:bg-zinc-200 transition-colors disabled:opacity-50">
                            {isLoading ? 'Sending...' : 'Send Recovery Signal'}
                        </button>
                        <div className="mt-4 text-center">
                            <button type="button" onClick={() => switchTo('LOGIN')} className="text-xs text-zinc-500 hover:text-white uppercase tracking-wider">
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {authView === 'RESET_SENT' && (
                    <div className="text-center space-y-6">
                        <p className="text-sm text-zinc-300 leading-relaxed">
                            Recovery instructions sent to <span className="text-white font-mono">{tempEmail}</span>.
                        </p>
                        <button onClick={() => switchTo('LOGIN')} className="w-full bg-white text-black font-bold uppercase tracking-[0.15em] py-3 rounded text-xs hover:bg-zinc-200 transition-colors">
                            Return to Login
                        </button>
                    </div>
                )}
             </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <LegalModal type={activeLegalModal} onClose={() => setActiveLegalModal(null)} />

    </div>
  );
};

export default LandingPage;
